import { Modal, Skeleton } from "@design";
import { ImageOff } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ScreenshotEntry } from "@/api";
import { Text } from "@/components/Text";
import { useScreenshotUrl } from "./useScreenshotUrl";

function ScreenshotImage({
  feedbackUuid,
  shot,
  className,
}: Readonly<{ feedbackUuid: string; shot: ScreenshotEntry; className: string }>) {
  const { t } = useTranslation("adminFeedback");
  const { url, isError } = useScreenshotUrl(feedbackUuid, shot.blobName);
  if (isError) {
    return (
      <span className="flex size-full items-center justify-center gap-2 p-2 text-content-tertiary">
        <ImageOff size={16} aria-hidden />
        <Text as="span" variant="caption" tone="tertiary">
          {t("detail.screenshotFailed")}
        </Text>
      </span>
    );
  }
  if (!url) return <Skeleton variant="block" className="size-full min-h-24" />;
  return <img src={url} alt={shot.originalFilename} className={className} />;
}

/** Thumbnails; clicking one opens it full size in a lightbox. */
export function FeedbackScreenshots({
  feedbackUuid,
  screenshots,
}: Readonly<{ feedbackUuid: string; screenshots: ScreenshotEntry[] }>) {
  const { t } = useTranslation("adminFeedback");
  const [open, setOpen] = useState<ScreenshotEntry | null>(null);
  return (
    <>
      <ul className="flex flex-wrap gap-3">
        {screenshots.map((shot) => (
          <li key={shot.blobName}>
            <button
              type="button"
              aria-label={t("detail.openScreenshot", { name: shot.originalFilename })}
              onClick={() => setOpen(shot)}
              className="block h-24 w-36 overflow-hidden rounded-lg border border-border-subtle bg-surface-secondary focus-visible:outline-2 focus-visible:outline-border-focus"
            >
              <ScreenshotImage feedbackUuid={feedbackUuid} shot={shot} className="size-full object-cover" />
            </button>
          </li>
        ))}
      </ul>
      <Modal open={open !== null} onClose={() => setOpen(null)} title={open?.originalFilename}>
        {open && (
          <ScreenshotImage
            feedbackUuid={feedbackUuid}
            shot={open}
            className="max-h-[70dvh] w-full rounded-lg object-contain"
          />
        )}
      </Modal>
    </>
  );
}
